import * as echarts from 'echarts/core';
import PSA from '../../psa';
import ItmMgr from '../../gui/ItmMgr';
import Utils from '../../utils/Utils';
import LoggingBase from '../../base/loggingbase';
import HtmHelper from '../../utils/HtmHelper';
import ChartOption from './ChartOption';

/**
 * class JsChart - client side implementation of JsChart control
 */
export default class JsChart extends LoggingBase {

	/**
	 * constructs a new instance
	 * @param {*} properties initialization properties
	 */
	constructor(properties) {
		super('widgets.jschart.JsChart');
		// setup this instance
		Utils.bindAll(this, [ "layout", "onReady", "onRender" ]);
		this._ready = false;
		this.parent = rap.getObject(properties.parent);
		this.wdgId = this.parent.getData("pisasales.CSTPRP.IDW");
		this.element = document.createElement('div');
		if ( this.wdgId ) {
			this.element.id = 'jsc' + this.wdgId;
		}
		else {
			this.error('Missing widget ID (pisasales.CSTPRP.IDW)!');
		}
		this.element.style.position = 'absolute';
		this.element.style.cursor = 'default';
		this.parent.append(this.element);
		this.parent.addListener("Resize", this.layout);
		// activate "render" event
		rap.on("render", this.onRender);
	}

	get ready() {
		return this._ready && (this.element instanceof HTMLElement);
	}

    /**
     * @inheritdoc
     * @override
     */
    destroy() {
        super.destroy();
    }

    /**
     * @inheritdoc
     * @override
     */
	doDestroy() {
		this._disposeChart();
		HtmHelper.rmvDomElm(this.element);
		this.parent = null;
		this.element = null;
		this._ready = false;
		super.doDestroy();
	}

	onReady() {
		this._ready = true;
	}

	onRender() {
		if ( this.element && this.element.parentNode ) {
			rap.off("render", this.onRender);
			this.onReady();
			this.layout();
		}
	}

	layout() {
		if ( this.ready ) {
			const area = this.parent.getClientArea();
			this.element.style.left = '0px';
			this.element.style.top = '0px';
			this.element.style.width = area[2] + 'px';
			this.element.style.height = area[3] + 'px';
			// a canvas chart does not auto-resize with its container - do it here
			if ( this.chart ) {
				this.chart.resize();
			}
		}
	}

	setChartData(args) {
		if ( this.ready ) {
			// what ever we got, the old content must go away; also reset the
			// centering styles a previous image render may have applied
			this._disposeChart();
			const element = this.element;
			element.innerHTML = '';
			element.style.display = '';
			element.style.alignItems = '';
			element.style.justifyContent = '';
			if ( args.chartData ) {
				this._renderChart(args.chartData);
			}
			else if ( args.image ) {
				this._renderImage(args.image);
			}
			else {
				this.debug('Nothing to render!', args);
			}
		}
	}

	/**
	 * renders the actual chart via ECharts
	 * @param {Object} chartData the chart data (metaData + chartData) as provided by the backend
	 */
	_renderChart(chartData) {
		if ( this.isDebugEnabled() ) {
			this.debug('Got chart data:', chartData);
		}
		// PSA.usrLng is already an ISO 639-1 code (fed via the client callback
		// widget), the same value moment.locale() is given elsewhere
		const locale = PSA.getInst().usrLng || 'en';
		const option = ChartOption.build(chartData, locale, this.element.clientWidth, this.element.clientHeight);
		if ( option ) {
			this.chart = echarts.init(this.element);
			this.chart.setOption(option);
			this.chart.on('click', (params) => {
				this._onChartClick(params);
			});
		}
		else {
			this.error('Could not build chart option from chart data.', chartData);
		}
	}

	/**
	 * handles an ECharts "click" event on a chart element (bar, pie slice, funnel
	 * segment, ...) and forwards it to the server. Only series-level clicks are
	 * forwarded - e.g. not legend/title clicks, which ECharts already handles
	 * itself. The backend does not yet send a stable element id in the chart
	 * JSON, so "id" is currently always <b>null</b>; it is already wired through
	 * so a future server-side id needs no further client change.
	 * @param {Object} params the ECharts click event parameters
	 */
	_onChartClick(params) {
		if ( params && (params.componentType === 'series') ) {
			const data = params.data;
			const par = {};
			par.id = (data && (data.id !== undefined)) ? data.id : null;
			par.name = params.name;
			par.value = params.value;
			par.seriesIndex = params.seriesIndex;
			par.seriesName = params.seriesName;
			par.dataIndex = params.dataIndex;
			par.componentSubType = params.componentSubType;
			par.btn = (params.event && params.event.event) ? params.event.event.button : 0;
			this._nfySrv('clicked', par);
		}
	}

	/**
	 * disposes the current ECharts instance, if any
	 */
	_disposeChart() {
		if ( this.chart ) {
			this.chart.dispose();
			this.chart = null;
		}
	}

	/**
	 * renders an image descriptor (bitmap, SVG or font icon) centered within this.element
	 * @param {Object} dsc the image descriptor as provided by the backend (ImgObj.toJson())
	 */
	_renderImage(dsc) {
		if ( this.isDebugEnabled() ) {
			this.debug('Got an image descriptor:', dsc);
		}
		// creImgObj() handles all three descriptor kinds (bitmap, SVG, font icon);
		// passing ics = 0 keeps the size carried by the descriptor itself (for a
		// font icon that is the size derived from the "_NNN" name suffix)
		const imd = ItmMgr.getInst().creImgObj(dsc, 0, false, false);
		if ( imd ) {
			// center the image within this.element
			this.element.style.display = 'flex';
			this.element.style.alignItems = 'center';
			this.element.style.justifyContent = 'center';
			this.element.appendChild(imd);
		}
	}

	_nfySrv(code, par) {
		if ( this.ready ) {
			const param = {};
			param.cod = code;
			param.par = par;
			rap.getRemoteObject(this).notify("JSC_NFY", param);
		}
	}

	/** register custom widget type */
	static register() {
		console.debug('Registering custom widget JsChart.');
		rap.registerTypeHandler("psawidget.JsChart", {
			factory : function(properties) {
				return new JsChart(properties);
			},
			destructor: "destroy",
			properties: [ "chartData" ],
			events: [ "JSC_NFY" ]
		});
	}
}
